/**
 * PayCollectionsHub.jsx — AR Collections management hub
 *
 * F-05 (APP-FIX-01): Refactored to Prashasta design system.
 * Previous dark-slate (#0f172a) theme replaced with Burgundy/Cream/Gold tokens.
 * All AR/finance functionality is preserved unchanged.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Banknote, Smartphone, ArrowLeftRight, Clock, AlertTriangle,
  Plus, RefreshCw, TrendingUp, CreditCard, Loader2,
  ChevronRight, Eye, IndianRupee, BarChart3
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (minor) =>
  `₹${((minor || 0) / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

/* ── Brand tokens (Prashasta design system) ─────────────────────────────── */
const B = {
  bg:       "#FAF6F0",
  cream:    "#FFFFFF",
  primary:  "#7A1A2B",
  gold:     "#C9A15F",
  border:   "#EDE8DE",
  text:     "#1C1917",
  muted:    "#78716C",
  error_bg: "#FDF2F5",
  error_bd: "#FECDD3",
};

const CHANNEL_ICONS = {
  CASH: Banknote, PHONEPAY: Smartphone, UPI: Smartphone,
  NEFT: ArrowLeftRight, RTGS: ArrowLeftRight, IMPS: ArrowLeftRight,
  BANK_TRANSFER: ArrowLeftRight, CHEQUE: CreditCard,
};
const CHANNEL_COLORS = {
  CASH: "#166534", PHONEPAY: "#6D28D9", UPI: "#0E7490",
  NEFT: "#1E40AF", RTGS: "#1E40AF", IMPS: "#0369A1",
  BANK_TRANSFER: "#1E40AF", CHEQUE: "#B45309",
};
const STATUS_STYLES = {
  PENDING_VERIFICATION: { bg: "#FEF9C3", color: "#92400E", label: "Pending" },
  VERIFIED:             { bg: "#DCFCE7", color: "#166534", label: "Verified" },
  PARTIALLY_ALLOCATED:  { bg: "#DBEAFE", color: "#1E40AF", label: "Part. Allocated" },
  FULLY_ALLOCATED:      { bg: "#D1FAE5", color: "#065F46", label: "Fully Allocated" },
  REVERSED:             { bg: "#FEE2E2", color: "#991B1B", label: "Reversed" },
};

/* ── Main page ───────────────────────────────────────────────────────────── */
export default function PayCollectionsHub() {
  const navigate   = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [tab,       setTab]       = useState("overview");

  const loadDashboard = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await axios.get(`${API}/api/ar/collections/dashboard`,
        { withCredentials: true });
      setDashboard(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Unable to load collections data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "pending",  label: `Pending (${dashboard?.receipts?.pending_verification || 0})` },
    { id: "overdue",  label: `Overdue (${dashboard?.overdue_count || 0})` },
    { id: "channels", label: "Channels" },
  ];

  return (
    <div data-testid="pay-collections-hub"
      style={{ background: B.bg, minHeight: "100vh", padding: "24px" }}>

      {/* ── Page header ────────────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px" }}>
        <div>
          <h1 style={{ color: B.primary, fontSize: "22px", fontWeight: 700, margin: 0 }}>
            Collections Hub
          </h1>
          <p style={{ color: B.muted, fontSize: "13px", margin: "3px 0 0" }}>
            {dashboard?.as_of ? `As of ${dashboard.as_of}` : "Payment collections management"}
          </p>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <button data-testid="btn-refresh-dashboard" onClick={loadDashboard}
            style={{ background: B.cream, border: `1px solid ${B.border}`, color: B.muted,
              borderRadius: "8px", padding: "8px 14px", cursor: "pointer",
              display: "flex", alignItems: "center", gap: "6px", fontSize: "13px" }}>
            <RefreshCw size={13} /> Refresh
          </button>
          <button data-testid="btn-record-payment" onClick={() => navigate("/ar/receipts")}
            style={{ background: B.primary, border: "none", color: "#fff",
              borderRadius: "8px", padding: "8px 16px", cursor: "pointer",
              display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: 600 }}>
            <Plus size={13} /> Record Payment
          </button>
        </div>
      </div>

      {/* ── Error ────────────────────────────────────────────────────────── */}
      {error && (
        <div style={{ background: B.error_bg, border: `1px solid ${B.error_bd}`,
          borderRadius: "8px", padding: "12px 16px", color: B.primary, marginBottom: "20px" }}>
          {error}
        </div>
      )}

      {/* ── Loading ──────────────────────────────────────────────────────── */}
      {loading ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center",
          padding: "60px", color: B.muted, gap: "10px" }}>
          <Loader2 size={20} style={{ color: B.primary, animation: "spin 1s linear infinite" }} />
          <span style={{ fontSize: "13px" }}>Loading collections data…</span>
        </div>

      /* ── Empty state ─────────────────────────────────────────────────── */
      ) : !dashboard ? (
        <div style={{ textAlign: "center", padding: "60px" }}>
          <BarChart3 size={36} style={{ color: B.gold, margin: "0 auto 12px" }} />
          <p style={{ color: B.muted, fontSize: "14px" }}>
            No collections data available. Record a receipt to get started.
          </p>
        </div>

      ) : (
        <>
          {/* ── KPI cards ───────────────────────────────────────────────── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "16px", marginBottom: "24px" }}>
            <KpiCard testId="kpi-total-outstanding"
              icon={<IndianRupee size={16} />} label="Total Outstanding"
              value={fmt(dashboard.outstanding?.total_minor)}
              sub={`${dashboard.outstanding?.count || 0} invoices`}
              accentColor="#B45309" />
            <KpiCard testId="kpi-collected-today"
              icon={<TrendingUp size={16} />} label="Collected Today"
              value={fmt(dashboard.collected_today_minor)}
              sub="Verified receipts" accentColor="#166534" />
            <KpiCard testId="kpi-pending-verification"
              icon={<Clock size={16} />} label="Pending Verification"
              value={dashboard.receipts?.pending_verification || 0}
              sub="Awaiting maker-checker" accentColor="#92400E" isCount />
            <KpiCard testId="kpi-overdue"
              icon={<AlertTriangle size={16} />} label="Overdue Invoices"
              value={dashboard.overdue_count || 0}
              sub="Past due date" accentColor="#991B1B" isCount />
          </div>

          {/* ── Tab bar ─────────────────────────────────────────────────── */}
          <div style={{ display: "flex", gap: "4px", marginBottom: "20px",
            borderBottom: `1px solid ${B.border}` }}>
            {tabs.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                style={{ background: "transparent", border: "none", padding: "10px 16px",
                  cursor: "pointer", fontSize: "13px",
                  fontWeight: tab === t.id ? 600 : 400,
                  color:     tab === t.id ? B.primary : B.muted,
                  borderBottom: tab === t.id ? `2px solid ${B.primary}` : "2px solid transparent",
                  marginBottom: "-1px" }}>
                {t.label}
              </button>
            ))}
          </div>

          {/* ── Tab panels ──────────────────────────────────────────────── */}
          {tab === "overview"  && <OverviewTab  dashboard={dashboard} navigate={navigate} />}
          {tab === "pending"   && <PendingTab   navigate={navigate} />}
          {tab === "overdue"   && <OverdueTab   navigate={navigate} />}
          {tab === "channels"  && <ChannelsTab  channels={dashboard.channel_breakdown_30d || []} />}
        </>
      )}
    </div>
  );
}

/* ── KPI card ─────────────────────────────────────────────────────────────── */
function KpiCard({ testId, icon, label, value, sub, accentColor, isCount }) {
  return (
    <div data-testid={testId}
      style={{ background: B.cream, borderRadius: "12px", padding: "20px",
        border: `1px solid ${B.border}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px",
        color: accentColor, marginBottom: "12px" }}>
        {icon}
        <span style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase",
          letterSpacing: "0.06em" }}>{label}</span>
      </div>
      <div style={{ color: B.text, fontSize: isCount ? "30px" : "22px", fontWeight: 700 }}>
        {value}
      </div>
      <div style={{ color: B.muted, fontSize: "12px", marginTop: "4px" }}>{sub}</div>
    </div>
  );
}

/* ── Overview tab ─────────────────────────────────────────────────────────── */
function OverviewTab({ dashboard, navigate }) {
  const recent = dashboard.recent_receipts || [];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
      {/* Recent receipts */}
      <div style={{ background: B.cream, borderRadius: "12px", padding: "20px",
        border: `1px solid ${B.border}` }}>
        <div style={{ display: "flex", justifyContent: "space-between",
          alignItems: "center", marginBottom: "16px" }}>
          <h3 style={{ color: B.text, fontSize: "14px", fontWeight: 600, margin: 0 }}>
            Recent Receipts
          </h3>
          <button onClick={() => navigate("/ar/receipts")}
            style={{ background: "transparent", border: "none",
              color: B.primary, fontSize: "12px", cursor: "pointer" }}>
            View all →
          </button>
        </div>
        {recent.length === 0 ? (
          <p style={{ color: B.muted, fontSize: "13px" }}>No receipts recorded yet.</p>
        ) : recent.map(r => (
          <ReceiptRow key={r.id || r._id} receipt={r} navigate={navigate} />
        ))}
      </div>

      {/* Quick actions */}
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        <div style={{ background: B.cream, borderRadius: "12px", padding: "20px",
          border: `1px solid ${B.border}` }}>
          <h3 style={{ color: B.text, fontSize: "14px", fontWeight: 600, margin: "0 0 14px" }}>
            Quick Actions
          </h3>
          {[
            { icon: <Banknote size={14} />, label: "Record Cash Payment",    color: "#166534", path: "/ar/receipts?channel=CASH",     testId: "quick-action-cash" },
            { icon: <Smartphone size={14} />, label: "Record PhonePe / UPI", color: "#6D28D9", path: "/ar/receipts?channel=PHONEPAY", testId: "quick-action-upi" },
            { icon: <ArrowLeftRight size={14} />, label: "Record Bank Transfer", color: "#1E40AF", path: "/ar/receipts?channel=NEFT", testId: "quick-action-bank" },
            { icon: <Eye size={14} />, label: "View Outstanding Invoices",   color: B.gold,    path: "/ar/invoices",                  testId: "quick-action-outstanding" },
          ].map(a => (
            <button key={a.label} data-testid={a.testId} onClick={() => navigate(a.path)}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                width: "100%", background: B.bg, border: `1px solid ${B.border}`,
                borderRadius: "8px", padding: "10px 14px", color: B.text,
                cursor: "pointer", marginBottom: "8px", fontSize: "13px" }}>
              <span style={{ display: "flex", alignItems: "center", gap: "8px", color: a.color }}>
                {a.icon} <span style={{ color: B.text }}>{a.label}</span>
              </span>
              <ChevronRight size={13} color={B.muted} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Receipt row ──────────────────────────────────────────────────────────── */
function ReceiptRow({ receipt, navigate }) {
  const st  = STATUS_STYLES[receipt.status] || STATUS_STYLES.PENDING_VERIFICATION;
  const Ch  = CHANNEL_ICONS[receipt.payment_channel] || CreditCard;
  const chColor = CHANNEL_COLORS[receipt.payment_channel] || B.muted;
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "10px 0", borderBottom: `1px solid ${B.border}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <Ch size={14} color={chColor} />
        <div>
          <div style={{ color: B.text, fontSize: "13px", fontWeight: 500 }}>
            {receipt.receipt_number || receipt.id}
          </div>
          <div style={{ color: B.muted, fontSize: "11px" }}>
            {receipt.receipt_date || ""} · {receipt.payment_channel || receipt.receipt_method || "—"}
          </div>
        </div>
      </div>
      <div style={{ textAlign: "right" }}>
        <div style={{ color: B.text, fontSize: "13px", fontWeight: 600 }}>
          {fmt(receipt.receipt_amount_minor)}
        </div>
        <span style={{ background: st.bg, color: st.color,
          fontSize: "10px", padding: "2px 6px", borderRadius: "4px" }}>
          {st.label}
        </span>
      </div>
    </div>
  );
}

/* ── Pending tab ──────────────────────────────────────────────────────────── */
function PendingTab({ navigate }) {
  const [receipts, setReceipts] = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/ar/receipts?status=PENDING_VERIFICATION&page_size=50`,
      { withCredentials: true })
      .then(r => setReceipts(r.data.receipts || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div style={{ background: B.cream, borderRadius: "12px",
      border: `1px solid ${B.border}`, overflow: "hidden" }}>
      <div style={{ padding: "16px 20px", borderBottom: `1px solid ${B.border}`,
        color: B.text, fontWeight: 600, fontSize: "14px" }}>
        Receipts Awaiting Verification ({receipts.length})
      </div>
      {loading
        ? <div style={{ padding: "24px", color: B.muted, textAlign: "center" }}>Loading…</div>
        : receipts.length === 0
          ? <div style={{ padding: "24px", color: B.muted, textAlign: "center" }}>
              No pending receipts
            </div>
          : receipts.map(r => (
            <div key={r.id || r._id} data-testid={`pending-receipt-${r.id || r._id}`}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "14px 20px", borderBottom: `1px solid ${B.border}`, cursor: "pointer" }}
              onClick={() => navigate(`/ar/receipts/${r.id || r._id}`)}>
              <div>
                <div style={{ color: B.text, fontSize: "13px", fontWeight: 500 }}>
                  {r.receipt_number} — {r.payment_channel || r.receipt_method}
                </div>
                <div style={{ color: B.muted, fontSize: "11px" }}>
                  Ref: {r.external_reference} · {r.receipt_date}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ color: "#92400E", fontWeight: 700, fontSize: "14px" }}>
                  {fmt(r.receipt_amount_minor)}
                </div>
                <span style={{ color: B.muted, fontSize: "11px" }}>Pending verification</span>
              </div>
            </div>
          ))}
    </div>
  );
}

/* ── Overdue tab ──────────────────────────────────────────────────────────── */
function OverdueTab({ navigate }) {
  const [invoices, setInvoices] = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/ar/collections/overdue?page_size=50`,
      { withCredentials: true })
      .then(r => setInvoices(r.data.invoices || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div style={{ background: B.cream, borderRadius: "12px",
      border: `1px solid ${B.border}`, overflow: "hidden" }}>
      <div style={{ padding: "16px 20px", borderBottom: `1px solid ${B.border}`,
        color: "#991B1B", fontWeight: 600, fontSize: "14px" }}>
        Overdue Invoices — Immediate Action Required ({invoices.length})
      </div>
      {loading
        ? <div style={{ padding: "24px", color: B.muted, textAlign: "center" }}>Loading…</div>
        : invoices.length === 0
          ? <div style={{ padding: "24px", color: B.muted, textAlign: "center" }}>No overdue invoices</div>
          : invoices.map(inv => (
            <div key={inv.id || inv._id} data-testid={`overdue-invoice-${inv.id || inv._id}`}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "14px 20px", borderBottom: `1px solid ${B.border}` }}>
              <div>
                <div style={{ color: B.text, fontSize: "13px", fontWeight: 500 }}>
                  {inv.invoice_number}
                </div>
                <div style={{ color: "#991B1B", fontSize: "11px" }}>
                  Due: {inv.due_date} · {inv.days_overdue} days overdue
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ color: "#991B1B", fontWeight: 700, fontSize: "14px" }}>
                  {fmt(inv.outstanding_minor)}
                </div>
                <span style={{ color: B.muted, fontSize: "11px" }}>{inv.collection_status}</span>
              </div>
            </div>
          ))}
    </div>
  );
}

/* ── Channels tab ─────────────────────────────────────────────────────────── */
function ChannelsTab({ channels }) {
  if (!channels.length) {
    return (
      <div style={{ textAlign: "center", padding: "32px", color: B.muted, fontSize: "13px" }}>
        No channel data for last 30 days.
      </div>
    );
  }
  const total = channels.reduce((s, c) => s + (c.total || 0), 0);
  return (
    <div style={{ background: B.cream, borderRadius: "12px",
      border: `1px solid ${B.border}`, padding: "20px" }}>
      <h3 style={{ color: B.text, fontSize: "14px", fontWeight: 600, margin: "0 0 16px" }}>
        Channel Breakdown — Last 30 Days
      </h3>
      {channels.map(ch => {
        const pct   = total > 0 ? Math.round((ch.total / total) * 100) : 0;
        const color = CHANNEL_COLORS[ch._id] || B.gold;
        return (
          <div key={ch._id} data-testid={`channel-row-${ch._id}`} style={{ marginBottom: "14px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
              <span style={{ color: B.text, fontSize: "13px", fontWeight: 500 }}>
                {ch._id || "Unknown"}
              </span>
              <span style={{ color: B.muted, fontSize: "12px" }}>
                {ch.count} receipts · {fmt(ch.total)} ({pct}%)
              </span>
            </div>
            <div style={{ background: B.border, borderRadius: "4px", height: "6px", overflow: "hidden" }}>
              <div style={{ background: color, width: `${pct}%`, height: "100%", borderRadius: "4px" }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
